/**
 * Auth screen — magic link flow with the GB aesthetic.
 * Step 1: enter email → request code
 * Step 2: enter 6-char code from email → verify
 *
 * Uses HTML inputs with the system keyboard (faster on mobile).
 * A button advances to the next step / submits.
 * B button goes back a step.
 */
import { useEffect, useRef, useState } from 'react';
import { authApi, ApiError } from '@/lib/api';
import { useApp } from '@/state/store';
import { sfx } from '@/lib/audio';
import { KeySprite, MailSprite, LoadingSprite, ArrowSprite } from '@/components/Sprites';

type Step = 'email' | 'code' | 'submitting';

export function AuthScreen() {
  const { setAuth, goto, showToast } = useApp();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const requestCode = async () => {
    if (!email.trim() || !email.includes('@')) {
      setError('Email inválido');
      sfx.error();
      return;
    }
    setError(null);
    setStep('submitting');
    try {
      const res = await authApi.requestCode(email.trim());
      if (res.success) {
        sfx.success();
        try { localStorage.setItem('vicin_pending_email', email.trim().toLowerCase()); } catch { /* */ }
        // En dev mode el backend devuelve el código en la respuesta
        // → lo guardamos en devCode para mostrarlo en pantalla
        // (NO lo pre-llenamos en el input para que el usuario lo tipee)
        if ('devCode' in res && res.devCode) {
          setDevCode(res.devCode);
        }
        setCode(''); // input vacío para que el usuario tipee
        setStep('code');
        showToast(
          res.devCode
            ? `DEV: código en pantalla`
            : '¡CÓDIGO ENVIADO! Revisá tu email',
          'success',
        );
      } else {
        setError(res.error || 'No se pudo enviar el código');
        sfx.error();
        setStep('email');
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Error de red';
      setError(msg);
      sfx.error();
      setStep('email');
    }
  };

  const verifyCode = async () => {
    const trimmed = code.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (trimmed.length !== 6) {
      setError('El código tiene 6 caracteres (A-Z, 2-9)');
      sfx.error();
      return;
    }
    setError(null);
    setStep('submitting');
    // Blur the active input BEFORE we transition away — otherwise the
    // GameBoyShell's focus listener keeps `.keyboard-open` set on the
    // wrap and the user sees only the LCD instead of the full handheld
    // after they land on the home screen.
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      (document.activeElement as HTMLElement).blur();
    }
    try {
      const res = await authApi.verifyCode(email.trim(), trimmed);
      if (res.success && res.user) {
        sfx.success();
        setAuth(res.user, res.community ?? null);
        showToast('¡Bienvenido!', 'success');
        setTimeout(() => goto('home'), 800);
      } else if (res.needs_registration) {
        setError('Necesitás registrarte primero. Pedile al admin un código de invitación.');
        sfx.error();
        setStep('code');
      } else {
        // Errores típicos: "Código inválido o expirado", "ya utilizado"
        const friendly = (res.error || '').toLowerCase().includes('utilizado')
          ? 'Ese código ya fue usado. Pedí uno nuevo.'
          : (res.error || 'Código inválido o expirado. Pedí uno nuevo.');
        setError(friendly);
        sfx.error();
        setStep('code');
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Error de red';
      setError(msg);
      sfx.error();
      setStep('code');
    }
  };

  // Hardware button handlers (A = submit, B = back)
  const handlersRef = useRef({ step, requestCode, verifyCode, setStep, setError: (m: string | null) => setError(m) });
  handlersRef.current = { step, requestCode, verifyCode, setStep, setError: (m: string | null) => setError(m) };

  useEffect(() => {
    const onA = () => {
      const s = handlersRef.current.step;
      if (s === 'email') void handlersRef.current.requestCode();
      else if (s === 'code') void handlersRef.current.verifyCode();
    };
    const onB = () => {
      if (handlersRef.current.step === 'code') {
        handlersRef.current.setError(null);
        handlersRef.current.setStep('email');
      }
    };
    window.addEventListener('vicin-gb:a', onA);
    window.addEventListener('vicin-gb:b', onB);
    return () => {
      window.removeEventListener('vicin-gb:a', onA);
      window.removeEventListener('vicin-gb:b', onB);
    };
  }, []);

  return (
    <div className="gb-screen">
      <div className="gb-status">
        <div className="gb-status-left">
          <span className="gb-status-dot" />
          <span className="gb-pix gb-pix-xs">VICIN</span>
        </div>
        <div className="gb-status-right">
          <span className="gb-pix gb-pix-xs">INICIAR</span>
        </div>
      </div>

      <div className="gb-body" style={{ padding: '4px 4px', overflow: 'hidden' }}>
        {step === 'email' && (
          <div
            className="gb-stack"
            style={{
              gap: 5,
              padding: '0',
              justifyContent: 'flex-start',
              height: '100%',
            }}
          >
            {/* Header: icon + title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <KeySprite size={16} />
              <div className="gb-pix" style={{ fontSize: 9, color: 'var(--gb-dark)' }}>
                INICIAR SESIÓN
              </div>
            </div>

            {/* Help text */}
            <div
              className="gb-pix gb-mid-text"
              style={{ fontSize: 6, lineHeight: 1.3 }}
            >
              Te enviaremos un código a tu email.
            </div>

            {/* Email field — native <input> for OS keyboard */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <label
                className="gb-pix"
                htmlFor="auth-email"
                style={{ fontSize: 6, color: 'var(--gb-mid)' }}
              >
                EMAIL
              </label>
              <input
                id="auth-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void requestCode(); }}
                placeholder="tu@email.com"
                className="gb-input"
                data-testid="auth-email"
              />
            </div>

            {/* Error */}
            {error && (
              <div
                role="alert"
                aria-live="polite"
                className="gb-pix"
                style={{
                  fontSize: 6,
                  color: 'var(--gb-bg)',
                  background: 'var(--gb-dark)',
                  padding: '3px 5px',
                  borderRadius: 2,
                  border: '1px solid var(--gb-dark)',
                }}
              >
                ⚠ {error}
              </div>
            )}

            {/* Small spacer — keeps the button visible without a huge gap */}
            <div style={{ flex: 0.3 }} />

            {/* Submit button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 5 }}>
              <span
                className="gb-pix gb-mid-text"
                style={{ fontSize: 5 }}
              >
                [A]
              </span>
              <button
                onClick={requestCode}
                className="gb-action"
                style={{
                  fontSize: 7,
                  padding: '5px 12px',
                  letterSpacing: 1,
                }}
                data-testid="auth-request-code"
              >
                ENVIAR
              </button>
            </div>
          </div>
        )}

        {step === 'code' && (
          <div
            className="gb-stack"
            style={{
              gap: 5,
              padding: '0',
              justifyContent: 'flex-start',
              height: '100%',
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <MailSprite size={16} />
              <div className="gb-pix" style={{ fontSize: 9, color: 'var(--gb-dark)' }}>
                VERIFICAR
              </div>
            </div>

            {/* Email destino */}
            <div
              className="gb-pix gb-mid-text"
              style={{ fontSize: 6, lineHeight: 1.3 }}
            >
              Código enviado a:
              <br />
              <strong style={{ color: 'var(--gb-dark)' }}>{email}</strong>
            </div>

            {/* Código dev display — solo se muestra si hay devCode (modo dev) */}
            {devCode && (
              <div
                data-testid="auth-dev-code-display"
                style={{
                  background: 'var(--gb-deepest)',
                  color: 'var(--gb-bg)',
                  padding: '4px 4px',
                  borderRadius: 3,
                  fontFamily: '"Press Start 2P", monospace',
                  fontSize: 10,
                  letterSpacing: 4,
                  textAlign: 'center',
                  border: '1px solid #0f380f',
                }}
              >
                {devCode}
              </div>
            )}

            {/* Code input — native <input> with inputmode=numeric for OS keyboard */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <label
                className="gb-pix"
                htmlFor="auth-code"
                style={{ fontSize: 6, color: 'var(--gb-mid)' }}
              >
                CÓDIGO (6 CHARS)
              </label>
              <input
                id="auth-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoCapitalize="characters"
                spellCheck={false}
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                onKeyDown={(e) => { if (e.key === 'Enter') void verifyCode(); }}
                placeholder="ABC123"
                className="gb-input gb-input--code"
                data-testid="auth-code"
              />
            </div>

            {/* Error */}
            {error && (
              <div
                role="alert"
                aria-live="polite"
                className="gb-pix"
                style={{
                  fontSize: 6,
                  color: 'var(--gb-bg)',
                  background: 'var(--gb-dark)',
                  padding: '3px 5px',
                  borderRadius: 2,
                  border: '1px solid var(--gb-dark)',
                }}
              >
                ⚠ {error}
              </div>
            )}

            <div style={{ flex: 0.3 }} />

            {/* Action buttons row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 5 }}>
              <button
                onClick={() => { sfx.cancel(); setStep('submitting'); void requestCode(); }}
                className="gb-pix gb-mid-text"
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: 5,
                  cursor: 'pointer',
                }}
                data-testid="auth-resend-code"
              >
                [A] REENVIAR
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span className="gb-pix gb-mid-text" style={{ fontSize: 5 }}>[A]</span>
                <button
                  onClick={verifyCode}
                  className="gb-action"
                  style={{
                    fontSize: 7,
                    padding: '5px 12px',
                    letterSpacing: 1,
                  }}
                  data-testid="auth-verify"
                >
                  ENTRAR
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 'submitting' && (
          <div className="gb-center">
            <LoadingSprite size={32} />
            <div className="gb-pix gb-pix-sm">CONECTANDO...</div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Hook used by the App to detect A and B presses for this screen. */
export function useAuthHandlers(
  step: Step,
  setStep: (s: Step) => void,
  email: string,
  code: string,
  requestCode: () => Promise<void>,
  verifyCode: () => Promise<void>,
) {
  return {
    onA: () => {
      if (step === 'email') void requestCode();
      else if (step === 'code') void verifyCode();
    },
    onB: () => {
      if (step === 'code') setStep('email');
    },
  };
}
